import {
  SHELFY_GENRE_OPTIONS,
  canonicalGenreValue,
  normalizeOptionLabel,
} from '../../util/shelfyOptions';

/**
 * Pure helpers for the My Shelf library experience.
 *
 * Everything here operates on the collection records returned by `/api/collection-items`
 * and on Sharetribe listing entities. Keeping it side-effect free makes the shelf
 * filtering, sorting, grouping, selling-state and ISO-matching logic directly testable.
 */

export const SHELF_STATUS_ON_SHELF = 'on-shelf';
export const SHELF_STATUS_READY = 'ready-to-rehome';
export const SHELF_STATUS_LISTED = 'listed';
export const SHELF_STATUS_PENDING = 'pending-approval';

export const READING_STATUS_UNREAD = 'unread';
export const READING_STATUS_READING = 'reading';
export const READING_STATUS_READ = 'read';

export const SALE_LISTING_TYPE = 'book-listing';
export const ISO_LISTING_TYPE = 'iso-book';

export const UNGROUPED_GENRE = 'Unshelved';

const humanizeGenre = value =>
  String(value || '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, letter => letter.toLocaleUpperCase());

/** Returns the canonical My Shelf group key without changing the stored book value. */
export const getCanonicalGenre = genre => {
  if (!genre) return UNGROUPED_GENRE;

  const normalized = normalizeOptionLabel(genre);
  const option = SHELFY_GENRE_OPTIONS.find(candidate =>
    [candidate.option, candidate.label, ...(candidate.aliases || [])].some(
      value => normalizeOptionLabel(value) === normalized
    )
  );

  return option?.option || genre;
};

/** Returns a canonical display label without changing the stored genre key. */
export const getGenreLabel = genre => {
  if (!genre) return UNGROUPED_GENRE;
  const normalized = normalizeOptionLabel(genre);
  const option = SHELFY_GENRE_OPTIONS.find(candidate =>
    [candidate.option, candidate.label, ...(candidate.aliases || [])].some(
      value => normalizeOptionLabel(value) === normalized
    )
  );
  return option?.label || humanizeGenre(genre) || UNGROUPED_GENRE;
};

export const STATUS_LABELS = {
  [SHELF_STATUS_ON_SHELF]: 'On my shelf',
  [SHELF_STATUS_READY]: 'Ready to rehome',
  [SHELF_STATUS_LISTED]: 'Listed for sale',
  [SHELF_STATUS_PENDING]: 'Pending approval',
};

export const SORT_OPTIONS = [
  { key: 'manual', label: 'Shelf order' },
  { key: 'recent', label: 'Recently added' },
  { key: 'title', label: 'Title A–Z' },
  { key: 'author', label: 'Author A–Z' },
  { key: 'genre', label: 'Genre' },
];

export const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: SHELF_STATUS_ON_SHELF, label: 'On my shelf' },
  { key: SHELF_STATUS_READY, label: 'Ready to rehome' },
  { key: SHELF_STATUS_LISTED, label: 'Listed for sale' },
  { key: READING_STATUS_READ, label: 'Read' },
  { key: READING_STATUS_UNREAD, label: 'Unread' },
];

export const normalizeText = value =>
  typeof value === 'string'
    ? value
        .toLocaleLowerCase()
        .replace(/[’']/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
        .replace(/\s+/g, ' ')
    : '';

export const normalizeIsbn = value =>
  typeof value === 'string'
    ? value
        .trim()
        .replace(/[-\s]/g, '')
        .toUpperCase()
    : '';

const listingIdString = listingId =>
  typeof listingId === 'string' ? listingId : listingId?.uuid || '';

const listingIsPublishedSale = listing => {
  const attributes = listing?.attributes || {};
  return (
    !attributes.deleted &&
    attributes.state === 'published' &&
    attributes.publicData?.listingType === SALE_LISTING_TYPE
  );
};

const listingAuthorName = listing =>
  listing?.attributes?.publicData?.author ||
  listing?.attributes?.publicData?.bookAuthor ||
  listing?.author?.attributes?.profile?.displayName ||
  '';

const sourceItemIdOf = listing => listing?.attributes?.privateData?.sourceShelfItemId || null;

const isSubmittedSaleListing = listing =>
  !listing?.attributes?.deleted &&
  ['published', 'pendingApproval'].includes(listing?.attributes?.state) &&
  listing?.attributes?.publicData?.listingType === SALE_LISTING_TYPE;

/**
 * Submitted listings created from a My Shelf record (recorded in the listing's privateData) whose
 * record is not yet linked to them, e.g. because the link request failed after publishing.
 *
 * @param {Array} collectionItems My Shelf records
 * @param {Array} ownListings the seller's own listings
 * @returns {Array<{ collectionItemId: string, listingId: string }>}
 */
export const findUnlinkedSourceListings = (collectionItems = [], ownListings = []) => {
  const itemsById = new Map(collectionItems.map(item => [item?.id, item]));
  return ownListings
    .filter(isSubmittedSaleListing)
    .map(listing => ({ listing, item: itemsById.get(sourceItemIdOf(listing)) }))
    .filter(({ listing, item }) => item && item.source_listing_id !== listingIdString(listing.id))
    .map(({ listing, item }) => ({
      collectionItemId: item.id,
      listingId: listingIdString(listing.id),
    }));
};

/**
 * Finds the seller's own active listing for an owned book.
 * Prefers the stored `source_listing_id` link, then falls back to an exact
 * normalized title+author match so books listed before this link existed still resolve.
 */
export const findListingForBook = (item, ownListings = []) => {
  const linkedId = item?.source_listing_id;
  if (linkedId) {
    const linked = ownListings.find(
      listing =>
        !listing?.attributes?.deleted &&
        listingIdString(listing.id) === linkedId &&
        ['published', 'pendingApproval'].includes(listing.attributes?.state) &&
        listing.attributes?.publicData?.listingType === SALE_LISTING_TYPE
    );
    if (linked) return linked;
  }

  const fromThisItem = item?.id
    ? ownListings.find(
        listing => isSubmittedSaleListing(listing) && sourceItemIdOf(listing) === item.id
      )
    : null;
  if (fromThisItem) return fromThisItem;

  const saleListings = ownListings.filter(listingIsPublishedSale);
  if (saleListings.length === 0) {
    return null;
  }

  const title = normalizeText(item?.title);
  const author = normalizeText(item?.author);
  if (!title) {
    return null;
  }

  return (
    saleListings.find(listing => {
      const listingTitle = normalizeText(listing?.attributes?.title);
      if (listingTitle !== title) {
        return false;
      }
      const otherAuthor = normalizeText(listingAuthorName(listing));
      return !author || !otherAuthor || otherAuthor === author;
    }) || null
  );
};

/** Derived display status. A live listing always wins over the stored shelf status. */
export const getBookStatus = (item, ownListings = []) => {
  const listing = findListingForBook(item, ownListings);
  if (listing?.attributes?.state === 'pendingApproval') {
    return SHELF_STATUS_PENDING;
  }
  if (listing) {
    return SHELF_STATUS_LISTED;
  }
  return item?.shelf_status === SHELF_STATUS_READY ? SHELF_STATUS_READY : SHELF_STATUS_ON_SHELF;
};

/**
 * Split sold inventory out of the active shelf. A book is sold when the server marked its record
 * (`sale_status`, from the purchase ledger) or when the private sold history links to it by
 * collection item or listing. Sold books never return to active My Shelf inventory.
 *
 * @param {Array} collectionItems My Shelf records
 * @param {Array} soldItems entries from `/api/my-shop/sold`
 * @returns {{ activeItems: Array, soldListingIds: Set<string> }}
 */
export const separateSoldInventory = (collectionItems = [], soldItems = []) => {
  const soldListingIds = new Set(soldItems.map(entry => entry?.listingId).filter(Boolean));
  const soldItemIds = new Set(soldItems.map(entry => entry?.collectionItemId).filter(Boolean));
  collectionItems.forEach(item => {
    if (item?.sale_status === 'sold' && item.source_listing_id) {
      soldListingIds.add(item.source_listing_id);
    }
  });

  const isSold = item =>
    item?.sale_status === 'sold' ||
    soldItemIds.has(item?.id) ||
    (!!item?.source_listing_id && soldListingIds.has(item.source_listing_id));

  return { activeItems: collectionItems.filter(item => !isSold(item)), soldListingIds };
};

/** Removes sold listings from the seller's active listing views. */
export const withoutSoldListings = (listings = [], soldListingIds = new Set()) =>
  listings.filter(listing => !soldListingIds.has(listingIdString(listing?.id)));

export const getGenre = item => {
  const genre = typeof item?.genre === 'string' ? item.genre.trim() : '';
  return genre || UNGROUPED_GENRE;
};

export const deriveShelfStats = (items = [], ownListings = []) => {
  const genres = new Set();
  let readyToRehome = 0;

  items.forEach(item => {
    const genre = typeof item?.genre === 'string' ? item.genre.trim() : '';
    if (genre) {
      genres.add(canonicalGenreValue(genre) || genre.toLocaleLowerCase());
    }
    const status = getBookStatus(item, ownListings);
    if (status === SHELF_STATUS_READY || status === SHELF_STATUS_LISTED) {
      readyToRehome += 1;
    }
  });

  return { bookCount: items.length, genreCount: genres.size, readyToRehome };
};

const uniqueSorted = values =>
  [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right));

export const collectGenres = () => SHELFY_GENRE_OPTIONS.map(option => option.option);

export const collectAuthors = (items = []) =>
  uniqueSorted(items.map(item => (typeof item?.author === 'string' ? item.author.trim() : '')));

const matchesStatusFilter = (item, status, ownListings) => {
  if (!status || status === 'all') {
    return true;
  }
  if (status === READING_STATUS_READ) {
    return item?.reading_status === READING_STATUS_READ;
  }
  if (status === READING_STATUS_UNREAD) {
    return !item?.reading_status || item.reading_status === READING_STATUS_UNREAD;
  }
  return getBookStatus(item, ownListings) === status;
};

export const filterCollection = (
  items = [],
  { search = '', genre = 'all', author = 'all', status = 'all', ownListings = [] } = {}
) => {
  const normalizedSearch = normalizeText(search);

  return items.filter(item => {
    if (genre !== 'all' && getCanonicalGenre(item?.genre) !== genre) {
      return false;
    }
    if (author !== 'all' && (item?.author || '').trim() !== author) {
      return false;
    }
    if (!matchesStatusFilter(item, status, ownListings)) {
      return false;
    }
    if (!normalizedSearch) {
      return true;
    }
    const haystack = normalizeText(
      [item?.title, item?.author, item?.series, item?.genre, item?.isbn].filter(Boolean).join(' ')
    );
    return haystack.includes(normalizedSearch);
  });
};

const byTitle = (left, right) => (left?.title || '').localeCompare(right?.title || '');

export const sortCollection = (items = [], sortKey = 'recent') => {
  const sorted = [...items];

  switch (sortKey) {
    case 'manual':
      return sorted.sort(
        (left, right) =>
          (left?.shelf_position ?? Number.MAX_SAFE_INTEGER) -
            (right?.shelf_position ?? Number.MAX_SAFE_INTEGER) ||
          (Date.parse(left?.created_at || '') || 0) - (Date.parse(right?.created_at || '') || 0)
      );
    case 'title':
      return sorted.sort(byTitle);
    case 'author':
      return sorted.sort(
        (left, right) =>
          (left?.author || '').localeCompare(right?.author || '') || byTitle(left, right)
      );
    case 'genre':
      return sorted.sort(
        (left, right) => getGenre(left).localeCompare(getGenre(right)) || byTitle(left, right)
      );
    case 'recent':
    default:
      return sorted.sort((left, right) => {
        const leftDate = Date.parse(left?.created_at || '') || 0;
        const rightDate = Date.parse(right?.created_at || '') || 0;
        return rightDate - leftDate || byTitle(left, right);
      });
  }
};

/** Groups books into genre shelves. `Unshelved` is always kept last. */
export const groupByGenre = (items = []) => {
  const shelves = new Map();
  const canonicalOrder = SHELFY_GENRE_OPTIONS.map(option => option.option);

  items.forEach(item => {
    const storedGenre = getGenre(item);
    const genre = getCanonicalGenre(storedGenre);

    if (!shelves.has(genre)) {
      shelves.set(genre, []);
    }
    shelves.get(genre).push(item);
  });

  return [...shelves.entries()]
    .map(([genre, books]) => ({ genre, label: getGenreLabel(genre), books }))
    .sort((left, right) => {
      if (left.genre === UNGROUPED_GENRE) return 1;
      if (right.genre === UNGROUPED_GENRE) return -1;
      const leftIndex = canonicalOrder.indexOf(left.genre);
      const rightIndex = canonicalOrder.indexOf(right.genre);
      if (leftIndex === -1 || rightIndex === -1) {
        return leftIndex === -1 && rightIndex === -1
          ? left.genre.localeCompare(right.genre)
          : leftIndex === -1
          ? 1
          : -1;
      }
      return leftIndex - rightIndex;
    });
};

/**
 * Matches an owned book against live ISO requests.
 *
 * ISO listings are Sharetribe `iso-book` listings. They carry a title and a `publicData.author`
 * but no ISBN field, so ISBN is used only when an ISO listing actually exposes one; otherwise
 * an exact normalized title match is required, plus an author match when both sides have one.
 */
export const matchIsoListings = (item, isoListings = [], { excludeAuthorId } = {}) => {
  const title = normalizeText(item?.title);
  const isbn = normalizeIsbn(item?.isbn);
  const author = normalizeText(item?.author);

  if (!title && !isbn) {
    return [];
  }

  return isoListings.filter(listing => {
    const attributes = listing?.attributes || {};
    if (attributes.deleted || attributes.state !== 'published') {
      return false;
    }
    if (attributes.publicData?.listingType !== ISO_LISTING_TYPE) {
      return false;
    }
    if (excludeAuthorId && listingIdString(listing?.author?.id) === excludeAuthorId) {
      return false;
    }

    const listingIsbn = normalizeIsbn(attributes.publicData?.isbn);
    if (isbn && listingIsbn) {
      return isbn === listingIsbn;
    }

    if (!title || normalizeText(attributes.title) !== title) {
      return false;
    }

    const listingAuthor = normalizeText(attributes.publicData?.author);
    return !author || !listingAuthor || listingAuthor === author;
  });
};
